// Bearer-token auth for the desktop (Electron) app. In a normal browser this
// module is inert (`isDesktop()` is false) and the app keeps using cookies.
//
// Google OAuth runs in the *system browser*, so the desktop renderer never
// receives a session cookie — it gets a bearer token (the better-auth session
// token) instead, which every api guard accepts via `auth.api.getSession`.
//
// The token is persisted by the MAIN process (userData/auth.json), NOT
// localStorage: the packaged renderer is served from an ephemeral
// http://localhost:<port> origin that changes every launch, and localStorage is
// origin-scoped — so it would be wiped on each restart. The main process seeds
// the saved token into the renderer at preload (`window.cotty.initialToken`)
// and we write changes back through `window.cotty.setToken`.

let memToken: string | undefined =
    typeof window !== 'undefined'
        ? window.cotty?.initialToken ?? undefined
        : undefined;

export const isDesktop = (): boolean =>
    typeof window !== 'undefined' && !!window.cotty;

// Where the OS paints the frameless window's controls over the renderer, so the
// app can keep that corner clear (see apps/desktop main.ts `titleBarOverlay`):
//   'right'  → minimize/maximize/close at the top-right (Windows/Linux)
//   'left'   → traffic lights at the top-left (macOS)
//   null     → a normal browser tab; nothing to reserve.
export const windowControlsSide = (): 'left' | 'right' | null => {
    if (typeof window === 'undefined' || !window.cotty) return null;
    return window.cotty.platform === 'darwin' ? 'left' : 'right';
};

export const getBearer = (): string | undefined =>
    isDesktop() ? memToken : undefined;

export const setBearer = (token: string): void => {
    memToken = token;
    window.cotty?.setToken?.(token);
};

export const clearBearer = (): void => {
    memToken = undefined;
    window.cotty?.setToken?.(null);
};
